import React, { useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import classNames from 'classnames';

import { Typography, Box, Button, TextField, MenuItem } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import EmptyState from 'common/components/EmptyState';
import SetPageTitle from 'components/Portals/SetPageTitle';
import Container from 'components/Container';
import Spinner from 'components/Spinner';
import OrdersList from './OrdersList';
import BackdropDialog from 'common/components/BackdropDialog';
import { without } from 'lodash';

import { useTranslation } from 'services/localization/useTranslation';
import {
	addPurchasedOrdersListener,
	getOrdersByConfirmationIdOrNames,
	setRentalsActiveTab,
} from 'actions';
import { getSearchResults } from 'common/utils/searchFunctions';
import {
	FilterOption,
	StatusFilter,
	PaymentFilter,
	filterOrders,
	filterOrdersByLocation,
} from './utils';

import { RentalsTabValues } from 'services/types';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as OrdersSelectors from 'selectors/OrdersSelectors';
import * as UserSelectors from 'selectors/UserSelectors';
import * as ViewSelectors from 'selectors/ViewSelectors';
import RentalSearchBarForCompleted from '../RentalsView/RentalSearchBar/RentalSearchBarForCompleted';
import * as ViewActions from 'actions/ViewActions';
import { removePurchasedOrdersInfoListener } from 'common/api/frontend/orders';
import LocationTypeSelect from 'components/LocationTypeSelect';
import { Callable } from 'common/frontend/callable';
import { useHasFeature } from 'hooks';

const INITIAL_LIMIT = 50;

const Orders = () => {
	const { t } = useTranslation();
	const classes = useStyles();
	const { hasFeature } = useHasFeature();

	const dispatch = useDispatch();
	const shopId = useSelector(ShopSelectors.activeShopId)!;
	const locationId = useSelector(UserSelectors.userActiveLocationId)!;
	const ordersData = useSelector(OrdersSelectors.ordersData);
	const ordersLoading = useSelector(OrdersSelectors.ordersLoading);
	const ordersError = useSelector(OrdersSelectors.ordersError);
	const shopLocationsCount = useSelector(ShopSelectors.allShopLocationsCount);
	const ordersViewLocationType = useSelector(ViewSelectors.ordersViewLocationType);

	const [ordersLimit, setOrdersLimit] = useState(INITIAL_LIMIT);
	const [searchInput, setSearchInput] = useState('');
	const [searchString, setSearchString] = useState('');
	const [selectedStatusFilter, setSelectedStatusFilter] = useState<StatusFilter>(StatusFilter.ALL);
	const [selectedPaymentFilter, setSelectedPaymentFilter] = useState<PaymentFilter>(
		PaymentFilter.ALL,
	);
	const [isOrderCancelDialogOpen, setIsOrderCancelDialogOpen] = useState(false);
	const [cancelOrderList, setCancelOrderList] = useState<string[]>([]);

	useEffect(() => {
		if (shopId) {
			dispatch(addPurchasedOrdersListener(shopId, ordersLimit));
		}
		return () => removePurchasedOrdersInfoListener();
	}, [shopId, ordersLimit, dispatch]);

	useEffect(() => {
		if (searchInput !== searchString && searchString.length > 0 && searchInput.length === 0) {
			setSearchString('');
		}
	}, [searchInput, searchString]);

	useEffect(() => {
		const activeTabValue = RentalsTabValues.SKIDATA;
		dispatch(setRentalsActiveTab(activeTabValue));
	}, [dispatch]);

	const statusFilterOptions: FilterOption<StatusFilter>[] = useMemo(
		() => [
			{ value: StatusFilter.ALL, label: t('orders.all', 'All') },
			{ value: StatusFilter.PENDING, label: t('orders.pending', 'Pending') },
			{ value: StatusFilter.COMPLETED, label: t('orders.completed', 'Completed') },
			{ value: StatusFilter.CANCELED, label: t('orders.cancelled', 'Cancelled') },
		],
		[t],
	);

	const paymentFilterOptions: FilterOption<PaymentFilter>[] = useMemo(
		() => [
			{ value: PaymentFilter.ALL, label: t('orders.allPaymentStatus', 'All payment statuses') },
			{ value: PaymentFilter.PAID, label: t('orders.paid', 'Paid') },
			{ value: PaymentFilter.UNPAID, label: t('orders.unpaid', 'Unpaid') },
		],
		[t],
	);

	const ordersByLocation = useMemo(
		() => filterOrdersByLocation(ordersData, locationId, ordersViewLocationType),
		[ordersData, locationId, ordersViewLocationType],
	);

	const filteredOrders = useMemo(
		() => filterOrders(ordersByLocation, selectedStatusFilter, selectedPaymentFilter),
		[ordersByLocation, selectedStatusFilter, selectedPaymentFilter],
	);

	const ordersToRender = useMemo(
		() =>
			!!searchString
				? getSearchResults(searchString, filteredOrders, [
						'NAME',
						'CONFIRMATION_NUMBER',
						'ORDER_BARCODE',
				  ])
				: filteredOrders,
		[searchString, filteredOrders],
	);

	const renderStatusFilterButton = (filterOption: FilterOption<StatusFilter>) => (
		<Button
			key={filterOption.value}
			className={classNames(
				classes.filterButton,
				filterOption.value === selectedStatusFilter && classes.filterButtonActive,
			)}
			onClick={() => setSelectedStatusFilter(filterOption.value)}
		>
			{filterOption.label}
		</Button>
	);

	const renderPaymentFilterOptions = () => (
		<TextField
			select
			variant="outlined"
			value={selectedPaymentFilter}
			onChange={(event) => setSelectedPaymentFilter(event.target.value as PaymentFilter)}
			InputProps={{
				classes: {
					root: classes.textField,
					notchedOutline: classes.textFieldOutline,
				},
			}}
		>
			{paymentFilterOptions.map((option) => (
				<MenuItem key={option.value} value={option.value}>
					{option.label}
				</MenuItem>
			))}
		</TextField>
	);

	const renderFilterOptions = () => (
		<Box mt={3} display="flex">
			{statusFilterOptions.map((option) => renderStatusFilterButton(option))}
			{renderPaymentFilterOptions()}
		</Box>
	);

	const renderSearchResultInfo = () => (
		<Box mt={1} mb={5}>
			<Typography variant="h6">
				{ordersToRender.length} {t('orders.foundOrdersAmount', 'orders were found')}
			</Typography>
		</Box>
	);

	const fetchWithSearchInput = () => {
		dispatch(getOrdersByConfirmationIdOrNames(shopId, searchInput));
		setSearchString(searchInput);
	};

	const handleCancel = async () => {
		const ordersToCancel = ordersToRender.filter((o) => cancelOrderList.includes(o.id));
		const cancelPromises = ordersToCancel.map((order) => {
			const { skidataProps } = order;
			if (!!skidataProps && !!skidataProps.DTAOrderStatus) {
				const { shopId, id } = order;
				return Callable.skidata.orders.cancel({
					shopId,
					orderId: id,
					skidataOrderStatus: skidataProps.DTAOrderStatus,
					skidataOrderId: skidataProps.orderId,
				});
			} else {
				return Promise.resolve(null);
			}
		});
		await Promise.all(cancelPromises);
		setCancelOrderList([]);
	};

	const handleRowCheckbox = (orderId: string) => {
		const newCancelOrderList = cancelOrderList.includes(orderId)
			? without(cancelOrderList, orderId)
			: cancelOrderList.concat(orderId);
		setCancelOrderList(newCancelOrderList);
	};

	const handleLocationTypeChange = (type: 'all' | 'current') =>
		dispatch(ViewActions.setOrdersViewLocationType(type));

	return (
		<>
			<SetPageTitle
				title="SKIDATA"
				rightContent={
					shopLocationsCount > 1 && hasFeature('BOOKINGS_VIEW_ALL_LOCATIONS_FILTER') ? (
						<LocationTypeSelect
							locationType={ordersViewLocationType}
							onLocationTypeChange={handleLocationTypeChange}
						/>
					) : undefined
				}
			/>
			<EmptyState
				title={t('orders.emptyOrdersTitle', 'You have no orders')}
				description={t(
					'orders.emptyOrdersDescription',
					"Once you have your first order, you'll be able to manage it here!",
				)}
				isLoading={ordersLoading}
				isEmpty={!ordersByLocation.length}
				render={() => (
					<Container className={classes.container}>
						{renderFilterOptions()}
						<Box mt={5}>
							<RentalSearchBarForCompleted
								inputValue={searchInput}
								onInputChange={setSearchInput}
								onSearchClick={fetchWithSearchInput}
							/>
						</Box>
						{(!!searchString ||
							selectedStatusFilter !== StatusFilter.ALL ||
							selectedPaymentFilter !== PaymentFilter.ALL) &&
							renderSearchResultInfo()}
						{cancelOrderList.length > 0 && (
							<Box display="flex" flexDirection="row-reverse" p={1} m={1}>
								<Box>
									<Button
										variant="outlined"
										color="secondary"
										className={classes.cancelOrdersBtn}
										onClick={() => setIsOrderCancelDialogOpen(true)}
									>
										{t('skidata.cancelDialogOpenBtn', {
											selectedItemsLength: cancelOrderList.length,
											defaultValue: 'Cancel ({{selectedItemsLength}}) selected orders',
										})}
									</Button>
								</Box>
							</Box>
						)}
						{!!ordersToRender.length && (
							<>
								<OrdersList
									orders={ordersToRender}
									handleRowCheckbox={handleRowCheckbox}
									cancelOrderList={cancelOrderList}
								/>
							</>
						)}
						<Button
							variant="contained"
							color="primary"
							onClick={() => setOrdersLimit(ordersLimit + 10)}
						>
							{ordersLoading ? (
								<Spinner relative />
							) : (
								t('orders.loadMoreOrders', 'Load more orders')
							)}
						</Button>
					</Container>
				)}
			/>
			<BackdropDialog
				isOpen={isOrderCancelDialogOpen}
				onClose={() => setIsOrderCancelDialogOpen(false)}
				label={t('skidata.cancelDialogLabel', 'Cancel SKIDATA order?')}
				body={
					<Typography variant="body1">
						{t(
							'skidata.cancelDialogContent',
							'This action will cancel the SKIDATA order, and cannot be undone. Remember to also refund the tickets. Do you want to cancel the order?',
						)}
					</Typography>
				}
				variant="danger"
				confirm={{
					label: t('skidata.cancelDialogConfirm', 'Yes, cancel order'),
					callback: handleCancel,
					closeAfterDone: true,
				}}
				cancel={{
					label: t('skidata.cancelDialogCancel', 'No, go back'),
				}}
			/>
			{ordersError && (
				<Typography variant="body2" color="error">
					{ordersError}
				</Typography>
			)}
		</>
	);
};

const useStyles = makeStyles((theme) =>
	createStyles({
		container: {
			height: 'auto',
			flex: 0,
			paddingTop: theme.spacing(2),
			paddingBottom: theme.spacing(1),
			[theme.breakpoints.down('sm')]: {
				paddingTop: theme.spacing(2),
				paddingBottom: theme.spacing(1),
			},
		},
		filterButton: {
			width: theme.spacing(12),
			height: theme.spacing(5),
			borderRadius: theme.spacing(0.5),
			boxShadow: `0 1px 3px 0 rgba(0, 0, 0, 0.12)`,
			color: theme.palette.primary.main,
			backgroundColor: theme.palette.background.paper,
			textTransform: 'none',
			transition: 'background-color 0.2s ease-in,color 0.2s ease-in',
			'&:not(:first-child)': {
				marginLeft: theme.spacing(1),
			},
			'&:hover': {
				backgroundColor: theme.palette.background.paper,
			},
		},
		filterButtonActive: {
			color: theme.palette.primary.contrastText,
			backgroundColor: theme.palette.primary.main,
			'&:hover': {
				backgroundColor: theme.palette.primary.main,
			},
		},
		textField: {
			width: 180,
			height: 40,
			fontSize: '1.4rem',
			marginLeft: theme.spacing(4),
		},
		textFieldOutline: {
			borderColor: theme.palette.colors.tundra.main,
		},
		cancelOrdersBtn: {
			color: theme.palette.error.main,
			borderColor: theme.palette.error.main,
		},
	}),
);

export default Orders;
